// Locale-aware formatting via the Intl APIs — replaces the three hand-rolled
// English relative-time formatters (SessionSidebar / ActivityBody / ResumeBanner)
// and the uncontrolled toLocale* date calls.

import { useCallback } from "react";

import { useLocale } from "./index.js";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

const toMs = (v: Date | number | string): number =>
  typeof v === "number" ? v : new Date(v).getTime();

/** Hook → `(when) => "5 minutes ago"` localized via Intl.RelativeTimeFormat.
 *  Pass `nowMs` for deterministic tests. */
export const useRelativeTime = (): ((
  when: Date | number | string,
  nowMs?: number,
) => string) => {
  const { locale } = useLocale();
  return useCallback(
    (when, nowMs) => {
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
      const diff = toMs(when) - (nowMs ?? Date.now()); // negative = past
      const abs = Math.abs(diff);
      if (abs < MINUTE) return rtf.format(0, "second"); // "now" / "jetzt"
      if (abs < HOUR) return rtf.format(Math.round(diff / MINUTE), "minute");
      if (abs < DAY) return rtf.format(Math.round(diff / HOUR), "hour");
      if (abs < WEEK) return rtf.format(Math.round(diff / DAY), "day");
      if (abs < MONTH) return rtf.format(Math.round(diff / WEEK), "week");
      if (abs < YEAR) return rtf.format(Math.round(diff / MONTH), "month");
      return rtf.format(Math.round(diff / YEAR), "year");
    },
    [locale],
  );
};

/** Hook → `(when) => "27 May 2026"` localized via Intl.DateTimeFormat. */
export const useFormatDate = (
  options: Intl.DateTimeFormatOptions = { dateStyle: "medium" },
): ((when: Date | number | string) => string) => {
  const { locale } = useLocale();
  return useCallback(
    (when) => new Intl.DateTimeFormat(locale, options).format(toMs(when)),
    [locale, options],
  );
};

/** Hook → `(n) => "1,234"` localized via Intl.NumberFormat. */
export const useFormatNumber = (
  options?: Intl.NumberFormatOptions,
): ((n: number) => string) => {
  const { locale } = useLocale();
  return useCallback(
    (n) => new Intl.NumberFormat(locale, options).format(n),
    [locale, options],
  );
};
